// Sports Coverage Expansion — Stage 0.1 client mirror of the
// public.sports registry (single source of truth lives in the DB;
// this is the pure, testable surface for phase/status decisions —
// same pattern as dna-relevance / archetypeSurface).
//
// TWO-AXIS MATURITY MODEL (2026-05-26 refactor).
//
//   status      — CAN this sport emit picks? ('live' / 'planned' / 'excluded')
//   calibrating — are we STILL gathering CLV/Brier evidence? (boolean)
//
// These are independent. A sport can be live AND still calibrating
// (founder-pilot pattern): the workspace publishes real picks, but the UI
// honestly badges "Live · calibrating" so users (or the founder) know the
// track-record is still being built. Picks emit; confidence labelling
// reflects the maturity gap.
//
// Today:
//   • football    — live, NOT calibrating (mature, full confidence)
//   • basketball  — live, calibrating (founder-pilot until Phase F gate clears)
//   • every other sport — planned (registry gate blocks slip emission)
//   • virtual_sports — permanently excluded
//
// The 'calibrating' status value is retained on SportStatus for legacy
// callsites + DB enum compatibility, but it's no longer used in the
// registry. New flips set `status: 'live', calibrating: true|false`.

export type SportStatus = "live" | "calibrating" | "planned" | "excluded";

export interface SportEntry {
  readonly code: string;
  readonly displayName: string;
  readonly phase: string;
  readonly status: SportStatus;
  /** Track-record still accruing? Independent of `status`. */
  readonly calibrating: boolean;
  readonly teamSport: boolean;
}

export const SPORTS: readonly SportEntry[] = [
  { code: "football", displayName: "Football", phase: "1", status: "live", calibrating: false, teamSport: true },
  { code: "basketball", displayName: "Basketball", phase: "2", status: "live", calibrating: true, teamSport: true },
  { code: "cricket", displayName: "Cricket", phase: "3A", status: "planned", calibrating: false, teamSport: true },
  { code: "tennis", displayName: "Tennis", phase: "3B", status: "planned", calibrating: false, teamSport: false },
  { code: "american_football", displayName: "American Football", phase: "4", status: "planned", calibrating: false, teamSport: true },
  { code: "baseball", displayName: "Baseball", phase: "4", status: "planned", calibrating: false, teamSport: true },
  { code: "ice_hockey", displayName: "Ice Hockey", phase: "4", status: "planned", calibrating: false, teamSport: true },
  { code: "table_tennis", displayName: "Table Tennis", phase: "5A", status: "planned", calibrating: false, teamSport: false },
  { code: "boxing_mma", displayName: "Boxing / MMA", phase: "5B", status: "planned", calibrating: false, teamSport: false },
  { code: "esports", displayName: "Esports", phase: "5C", status: "planned", calibrating: false, teamSport: true },
  { code: "volleyball", displayName: "Volleyball", phase: "5D", status: "planned", calibrating: false, teamSport: true },
  { code: "rugby", displayName: "Rugby", phase: "5D", status: "planned", calibrating: false, teamSport: true },
  { code: "darts", displayName: "Darts", phase: "5D", status: "planned", calibrating: false, teamSport: false },
  { code: "snooker", displayName: "Snooker", phase: "5D", status: "planned", calibrating: false, teamSport: false },
  { code: "golf", displayName: "Golf", phase: "5D", status: "planned", calibrating: false, teamSport: false },
  { code: "motor_racing", displayName: "Motor Racing", phase: "5D", status: "planned", calibrating: false, teamSport: false },
  { code: "handball", displayName: "Handball", phase: "5D", status: "planned", calibrating: false, teamSport: true },
  { code: "horse_racing", displayName: "Horse Racing", phase: "6", status: "planned", calibrating: false, teamSport: false },
  { code: "virtual_sports", displayName: "Virtual Sports", phase: "never", status: "excluded", calibrating: false, teamSport: false },
] as const;

const BY_CODE: ReadonlyMap<string, SportEntry> = new Map(
  SPORTS.map((s) => [s.code, s]),
);

export function sportByCode(code: string | null | undefined): SportEntry | null {
  if (!code) return null;
  return BY_CODE.get(code) ?? null;
}

/** Only a sport whose registry status is 'live' may generate slips.
 *  Unknown / planned / calibrating / excluded ⇒ false (honest). */
export function isSportLive(code: string | null | undefined): boolean {
  return sportByCode(code)?.status === "live";
}

/** Is the sport STILL accruing CLV/Brier evidence? Independent of `status` —
 *  a live sport can also be calibrating (founder-pilot pattern). Returns
 *  false for unknown sports (no maturity claim either way). */
export function isSportCalibrating(code: string | null | undefined): boolean {
  return sportByCode(code)?.calibrating === true;
}

/** Permanently excluded sports (never built). Used by the
 *  cross-phase never-build guard. */
export function isSportExcluded(code: string | null | undefined): boolean {
  return sportByCode(code)?.status === "excluded";
}

export function liveSports(): SportEntry[] {
  return SPORTS.filter((s) => s.status === "live");
}
